import { GraduationCap, Clock, BarChart3, Check, Award, ArrowRight, BookOpen, Building2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Reveal, MaskedLine, SectionHeading } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { COURSES, ACADEMY_FACILITIES } from "@/data/static";

const MATERI = [
  "Fundamental hair anatomy & konsultasi klien",
  "Teknik guntingan klasik hingga tren Korea",
  "Formulasi warna, bleaching & koreksi warna",
  "Styling, blow dry & hair spa profesional",
  "Manajemen salon & pelayanan pelanggan premium",
  "Personal branding & strategi buka usaha sendiri",
];

export default function Academy() {
  const { waLink } = useSettings();
  return (
    <div data-testid="academy-page" className="pt-32 pb-0">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-20">
        <MaskedLine delay={0.1}><p className="overline-label">Salon Strawberry Academy</p></MaskedLine>
        <h1 className="mt-4 font-cinzel font-black text-brand-burgundy text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          <MaskedLine delay={0.25}>DARI NOL HINGGA PRO</MaskedLine>
          <MaskedLine delay={0.4} innerClassName="text-gold-gradient">ACADEMY & PAKET USAHA SALON.</MaskedLine>
        </h1>
        <MaskedLine delay={0.55} className="mt-6">
          <p className="max-w-2xl text-base leading-relaxed text-brand-ink-muted">
            Pelatihan lengkap dari dasar hingga mahir, dimentori langsung oleh tim profesional. Sertifikat resmi, praktik nyata, dan pendampingan bisnis. Pilih kursus saja atau langsung miliki paket usaha lengkap — tempat, alat, izin, dan bimbingan.
          </p>
        </MaskedLine>
      </div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-sm gold-foil-border" data-testid="academy-facility-photo">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/files/salon-beauty-bar/uploads/b0d9c44e-f69c-46b0-b6e2-99b57afe4e48.jpg`}
              alt="Ruang kelas dan praktek Salon Strawberry Academy"
              loading="lazy"
              className="h-72 w-full object-cover sm:h-96 lg:h-[28rem]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8">
              <p className="overline-label !text-brand-gold-warm">Fasilitas Asli Kami</p>
              <p className="mt-2 font-serif text-xl sm:text-2xl text-brand-cream">Ruang Kelas & Praktek Academy</p>
            </div>
          </div>
        </Reveal>
      </div>

      <Marquee items={["SALON ACADEMY", "SERTIFIKAT RESMI", "PRAKTEK LIVE MODEL", "BIMBINGAN BUKA USAHA", "MENTOR BERPENGALAMAN", "FREE TOOLKIT PROFESIONAL"]} />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <SectionHeading index="01" overline="Program Kursus" title="Pilih Jalur Keahlianmu" />
        <div className="grid gap-7 md:grid-cols-2" data-testid="academy-courses">
          {COURSES.map((c, i) => (
            <Reveal key={c.title} delay={(i % 2) * 0.12}>
              <div
                data-testid={`course-card-${i}`}
                className={`group flex h-full flex-col rounded-sm p-9 transition-all duration-500 hover:-translate-y-2 ${
                  i === 0
                    ? "bg-brand-burgundy text-brand-cream shadow-[0_25px_60px_rgba(74,14,23,0.3)]"
                    : "bg-white gold-foil-border hover:shadow-[0_25px_60px_rgba(74,14,23,0.15)]"
                }`}
              >
                {i === 0 && (
                  <span className="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-brand-gold px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-ink">
                    <Award size={12} /> Paling Populer
                  </span>
                )}
                <h3 className={`font-serif text-2xl sm:text-3xl font-semibold ${i === 0 ? "text-brand-cream" : "text-brand-burgundy"}`}>{c.title}</h3>
                <div className="mt-4 flex flex-wrap gap-4 text-xs uppercase tracking-[0.15em]">
                  <span className={`flex items-center gap-2 ${i === 0 ? "text-brand-gold-warm" : "text-brand-gold"}`}><Clock size={13} /> {c.duration}</span>
                  <span className={`flex items-center gap-2 ${i === 0 ? "text-brand-gold-warm" : "text-brand-gold"}`}><BarChart3 size={13} /> {c.level}</span>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {c.benefits.map((b) => (
                    <li key={b} className={`flex gap-3 text-sm ${i === 0 ? "text-brand-cream/80" : "text-brand-ink-muted"}`}>
                      <Check size={15} className="mt-0.5 shrink-0 text-brand-gold" /> {b}
                    </li>
                  ))}
                </ul>
                <div className={`mt-auto flex items-center justify-between border-t pt-6 mt-8 ${i === 0 ? "border-brand-gold/25" : "border-brand-gold/20"}`}>
                  <div>
                    <p className={`text-[10px] uppercase tracking-[0.25em] ${i === 0 ? "text-brand-cream/50" : "text-brand-ink-muted"}`}>Investasi</p>
                    <p className={`font-serif text-2xl font-bold ${i === 0 ? "text-brand-gold" : "text-gold-gradient"}`}>{c.price}</p>
                  </div>
                  <a
                    data-testid={`course-register-${i}`}
                    href={waLink(`Halo Salon Strawberry Academy! Saya ingin mendaftar kursus: ${c.title} (${c.price}).`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-all duration-300 ${
                      i === 0
                        ? "bg-brand-gold text-brand-ink hover:bg-brand-gold-bright"
                        : "bg-brand-burgundy text-brand-cream hover:bg-brand-gold hover:text-brand-ink"
                    }`}
                  >
                    Daftar <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-24 grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading index="02" overline="Materi Pembelajaran" title="Kurikulum Komprehensif" />
            <div className="space-y-4" data-testid="academy-materi">
              {MATERI.map((m, i) => (
                <Reveal key={m} delay={i * 0.07}>
                  <div className="flex items-center gap-5 rounded-sm bg-white gold-foil-border p-5 transition-colors duration-300 hover:bg-brand-paper">
                    <span className="font-cinzel text-xl font-bold text-brand-gold">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm sm:text-base text-brand-ink">{m}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading index="03" overline="Fasilitas & Sertifikat" title="Belajar dengan Standar Salon Mewah" />
            <div className="space-y-4" data-testid="academy-fasilitas">
              {ACADEMY_FACILITIES.map((f, i) => (
                <Reveal key={f} delay={i * 0.07}>
                  <div className="flex items-center gap-5 rounded-sm bg-brand-burgundy-light p-5">
                    <Building2 size={18} className="shrink-0 text-brand-burgundy" />
                    <p className="text-sm sm:text-base text-brand-ink">{f}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <div className="mt-6 flex items-start gap-5 rounded-sm border-2 border-dashed border-brand-gold/50 bg-brand-paper p-6" data-testid="academy-sertifikat">
                <GraduationCap size={32} className="shrink-0 text-brand-gold" />
                <div>
                  <p className="font-serif text-lg font-semibold text-brand-burgundy">Sertifikat Kelulusan Resmi</p>
                  <p className="mt-1 text-sm text-brand-ink-muted">Setiap lulusan menerima sertifikat resmi Salon Strawberry Academy yang diakui mitra salon & klinik kecantikan kami.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-24 mb-24">
          <div className="rounded-sm bg-brand-ink px-8 py-14 text-center sm:px-16" data-testid="academy-cta">
            <BookOpen size={36} className="mx-auto text-brand-gold" />
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl font-semibold text-brand-cream">Kelas Baru Dibuka Setiap Bulan</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-brand-cream/60">Kuota terbatas hanya 10 siswa per angkatan agar praktek maksimal. Amankan kursi Anda hari ini.</p>
            <a
              data-testid="academy-register-btn"
              href={waLink("Halo Salon Strawberry Academy! Saya ingin informasi pendaftaran kursus.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-brand-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-ink transition-all duration-300 hover:bg-brand-gold-bright"
            >
              Daftar Sekarang <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
